<template>
  <div class="product-preview">
    <div class="product-preview__header">
      <div class="product-preview__header__actions">
        <a-icon
          type="arrow-left"
          @click="onCloseProductDetails()"
        />
        {{ $t("offers.product_view") }}
      </div>

      <div class="product-preview__header__title">
        <template v-if="product?.p_code">
          <ProductCode
            :code="product?.p_code"
            :slug="product?.slug"
          />
          {{ product?.name }}
        </template>

        <a-skeleton
          v-else
          size="small"
        />
      </div>

      <a-divider class="product-preview__header__divider" />
    </div>

    <div>
      <a-skeleton :loading="!product?.p_code">
        <ProductGallery :images="productImages" />
      </a-skeleton>
    </div>

    <div class="product-section">
      <div class="product-section__heading">
        <a-divider />
        {{ $t("offers.create_form_fields.description") }}
      </div>

      <a-skeleton :loading="!product?.p_code">
        <div
          class="description-html"
          v-html="product?.description"
        />
      </a-skeleton>
    </div>

    <div class="product-section">
      <div class="product-section__heading">
        <a-divider />
        {{ $t("offers.create_form_fields.attributes") }}
      </div>
      <a-skeleton :loading="!product?.p_code" />

      <div>
        <div
          v-for="(attr, index) in productAttributes"
          :key="index"
          class="product-section__attribute"
          :class="{ 'product-section__attribute--even': index % 2 === 0 }"
        >
          <div class="product-section__attribute__label">{{ attr.name }}</div>
          <div class="product-section__attribute__value">
            {{ attr.value ? attr.value.map(({ label }) => label).join(", ") : "-" }}
          </div>
        </div>
      </div>

      <div class="product-section__footer">
        <a-button
          type="primary"
          icon="plus"
          @click="onSelect"
        >
          {{ $t("offers.make_offer") }}
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, onMounted, ref, shallowRef, watch } from "vue"

import ProductCode from "@/components/Common/ProductCode"
import type { ExistedProduct, ExistedProductAttribute } from "@/modules/offers/types/catalog"
import offersService from "@/services/offersService"

import ProductGallery from "./ProductGallery.vue"

type Props = {
  choosenExistingProduct: ExistedProduct
}
type Emits = {
  (event: "closeProductDetails"): void
  (event: "selectExistedProduct", product: ExistedProduct): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const product = ref<ExistedProduct>()
const isFetching = shallowRef<boolean>(true)

const productImages = computed<Array<string>>(() => product.value?.images || [])
const productAttributes = computed<Array<ExistedProductAttribute>>(() => [
  ...(product.value?.attrs_main || []),
  ...(product.value?.attrs_full || [])
])

const setupProduct = async () => {
  product.value = undefined

  try {
    isFetching.value = true

    const { data } = await offersService.fetchProductInfo({
      code: props.choosenExistingProduct.p_code
    })
    product.value = data
  } finally {
    isFetching.value = false
  }
}
const onCloseProductDetails = () => {
  emit("closeProductDetails")
}
const onSelect = () => {
  emit("selectExistedProduct", product.value)
  onCloseProductDetails()
}

watch(() => props.choosenExistingProduct.p_code, setupProduct)

onMounted(setupProduct)
</script>

<style lang="scss" scoped>
.product-preview {
  position: relative;

  display: flex;
  flex-flow: column nowrap;
  gap: 24px;

  padding: 0 16px 16px 16px;
  height: 100%;
  overflow: auto;

  &__header {
    position: sticky;
    top: 0;
    z-index: 1;

    display: flex;
    flex-flow: column nowrap;
    gap: 16px;

    background: white;

    &__actions {
      display: flex;
      align-items: center;
      gap: 16px;

      font-size: 16px;
      height: 36px;
    }

    &__title {
      display: flex;
      flex-flow: column nowrap;
      gap: 4px;

      font-size: 14px;
    }

    &__divider {
      margin: 0;
    }
  }
}
.product-section {
  &__heading {
    display: flex;
    align-items: center;
    gap: 16px;

    & > :first-child {
      min-width: unset;
      width: 20px;
    }
  }

  &__attribute {
    display: flex;
    padding: 12px;

    & > * {
      width: 50%;
    }

    &--even {
      background: $gray-color-4;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;

    padding: 16px 0;
  }
}

.description-html {
  font-size: 15px;
  font-weight: 400;
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 0;
    font-weight: 500;
    margin-bottom: 0.8rem;
  }
  p {
    margin-bottom: 1.6rem;
  }
}
</style>
